import { useCallback, useEffect, useState } from 'react';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Field } from '~/evifields/src/types/Field';
import { FieldId } from '~/evifields/src/types/FieldId';
import { api } from '~/redux/api/createApi';
import { ContextType } from '~/redux/api/methods/userPreferences';

export function usePinnedFilters(
  fields: Record<FieldId, Field> = {},
  context: ContextType = 'SEARCH',
) {
  const [
    getPinnedFilters,
    { data: serverPinnedFilters, isFetching, isSuccess },
  ] = api.endpoints.getPinnedFilters.useLazyQuery();
  const [updatePinnedFilters] = api.endpoints.updatePinnedFilters.useMutation();
  const [localPinnedFilters, setLocalPinnedFilters] = useState<
    Array<number | string>
  >([]);
  const { toast } = useToast();

  useEffect(() => {
    const result = [];
    if (Object.values(fields).length > 0 && serverPinnedFilters?.data) {
      for (const filterId of serverPinnedFilters?.data ?? []) {
        const field = fields[filterId];
        if (field) {
          result.push(isNaN(Number(field.id)) ? field.id : Number(field.id));
        }
      }
      setLocalPinnedFilters(result);
    }
  }, [serverPinnedFilters, fields]);

  const updatePinnedFiltersLocal = useCallback(
    async (pinnedFilters: Array<string | number>) => {
      setLocalPinnedFilters(pinnedFilters);
      try {
        await updatePinnedFilters({
          pinnedFilters: pinnedFilters.map(String),
          context,
        }).unwrap();
      } catch {
        toast({
          status: 'danger',
          message: 'Error updating pinned filters',
        });
      }
    },
    [updatePinnedFilters, getPinnedFilters, context],
  );

  return {
    updatePinnedFilters: updatePinnedFiltersLocal,
    getPinnedFilters: () => getPinnedFilters(context),
    pinnedFiltersIds: localPinnedFilters,
    isFetching,
    isSuccess,
  };
}
